import { FC, useContext, useEffect, useRef, useState } from "react";
// eslint-disable-next-line no-restricted-imports
import { toast } from "react-toastify";

import { WebsocketStatusType } from "../../shared/types/pubsub";
import { AUTH_ERRORS } from "../../shared/types/websocket-auth";

import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import {
  CloudConnectedIcon,
  CloudConnectingIcon,
  CloudDisconnectedIcon,
  RefreshIcon,
} from "mds/icons";
import { IconProps } from "mds/utils/images";
import { PubsubWebsocketContext } from "providers/PubsubWebsocketProvider";
import { ShareDBContext } from "providers/ShareDBProvider";
import { store, useAppSelector } from "store/index";
import { selectCurrentUserId, selectShareDBOpStatus } from "store/selectors";
import { isLocalEnv } from "utils/env";
import { logInfoWithDebugFlag } from "utils/logger";
import { getWindowId } from "utils/window";

// After this threshold the connection is considered delayed and an
// error banner is shown to the user.
const SHAREDB_DELAY_THRESHOLD_MS = 5000;
// ShareDB is critical for document editing, so we show errors quickly
const SHAREDB_DISCONNECT_TOAST_DELAY_MS = 3000;
// Pubsub is less critical (model updates, attendance), so we're more tolerant
const PUBSUB_DISCONNECT_TOAST_DELAY_MS = 10000;
const ERROR_TOAST_ID = "websocket-disconnected";

type ConnectionStateType = "connected" | "connecting" | "disconnected" | "saving";

/**
 * Active states for the saving status of a document / sharedb operation.
 * null indicates that the document is saved and no special status should be shown.
 */

type NonNullDocSavingStatusType = "delayed" | "saving";
type DocSavingStatusType = NonNullDocSavingStatusType | null;

const SAVING_STATUS: Record<string, DocSavingStatusType> = {
  DELAYED: "delayed",
  SAVING: "saving",
};

const WEBSOCKET_STATE_TO_ICON_STATE: Record<
  WebsocketStatusType | NonNullDocSavingStatusType,
  ConnectionStateType
> = {
  connected: "connected",
  delayed: "connecting",
  // Change the indicator for integration tests so we don't have flakey tests due to the rotating icon.
  saving: isLocalEnv ? "connected" : "saving",
  connecting: isLocalEnv ? "connected" : "connecting",
  disconnected: "disconnected",
  stopped: "disconnected",
  closed: "disconnected",
  "401": "disconnected",
};

const ICON_STATE_TO_COLOR: Record<ConnectionStateType, string> = {
  connecting: "text-orange",
  connected: "text-green",
  disconnected: "text-red",
  saving: "text-blue",
};

const ICON_STATE_TO_ICON_COMPONENT: Record<ConnectionStateType, FC<IconProps>> = {
  saving: RefreshIcon,
  connecting: CloudConnectingIcon,
  connected: CloudConnectedIcon,
  disconnected: CloudDisconnectedIcon,
};

const ICON_STATE_TO_TOOLTIP: Record<ConnectionStateType, string> = {
  connected: t("websocket.saving_indicator.connected"),
  connecting: t("websocket.saving_indicator.connecting"),
  disconnected: t("websocket.saving_indicator.disconnected"),
  saving: t("websocket.saving_indicator.saving"),
};

type ConnectionIconProps = {
  iconState: ConnectionStateType;
  classOverride?: string;
};

export const ConnectionIcon = ({ iconState, classOverride }: ConnectionIconProps) => {
  const Icon = ICON_STATE_TO_ICON_COMPONENT[iconState];
  const colorClass = ICON_STATE_TO_COLOR[iconState];
  const tooltipMessage = ICON_STATE_TO_TOOLTIP[iconState];

  const ariaLabel = t("websocket.saving_indicator.aria_label", { status: tooltipMessage });
  const IconWithTooltipElement = (
    <Tooltip text={tooltipMessage}>
      <Icon aria-label={ariaLabel} className={classOverride || colorClass} size="l" />
    </Tooltip>
  );

  // When saving we want the icon to rotate indefinitely to indicate activity.
  if (iconState === "saving") {
    return <div className="animate-rotate-clockwise">{IconWithTooltipElement}</div>;
  }

  return IconWithTooltipElement;
};

type ConnectionStatusIndicatorProps = {
  className?: string;
};

/**
 * Represents both the current connection status to the websocket/server,
 * and also displays saving status indication for any document currently open.
 */
/**
 * Determines if a websocket status indicates a connection problem
 */
const isDisconnectedStatus = (status: WebsocketStatusType): boolean => {
  return (
    status === "disconnected" || status === "stopped" || status === "closed" || status === "401"
  );
};

/**
 * Gets the overall connection status by combining ShareDB and pubsub statuses.
 * ShareDB status takes priority since it's critical for document editing.
 * If ShareDB is not available (e.g., on home page), only uses pubsub status.
 */
const getOverallConnectionStatus = (
  shareDBStatus: WebsocketStatusType | null,
  pubsubStatus: WebsocketStatusType,
  savingStatus: DocSavingStatusType,
  opError: string | null,
  isOverwriteError: boolean,
): WebsocketStatusType | NonNullDocSavingStatusType => {
  // Op errors take highest priority (except overwrite errors)
  if (opError && !isOverwriteError) {
    return "disconnected";
  }

  // Saving status takes priority over connection status
  if (savingStatus) {
    return savingStatus;
  }

  // If ShareDB is not available (e.g., on home page), only use pubsub status
  if (shareDBStatus === null) {
    return pubsubStatus;
  }

  // If either connection is disconnected, show disconnected
  // ShareDB disconnection is more critical, so check it first
  if (isDisconnectedStatus(shareDBStatus)) {
    return shareDBStatus;
  }
  if (isDisconnectedStatus(pubsubStatus)) {
    return pubsubStatus;
  }

  // If either is connecting, show connecting (prioritize ShareDB)
  if (shareDBStatus === "connecting") {
    return shareDBStatus;
  }
  if (pubsubStatus === "connecting") {
    return pubsubStatus;
  }

  // Both are connected
  return "connected";
};

export const ConnectionStatusIndicator: FC<ConnectionStatusIndicatorProps> = ({ className }) => {
  const shareDBContext = useContext(ShareDBContext);
  const pubsubContext = useContext(PubsubWebsocketContext);
  // ShareDB may not be available on pages like home page (only mounted in section routes)
  const shareDBStatus = shareDBContext?.shareDBStatus ?? null;
  const pubsubStatus = pubsubContext?.pubsubStatus ?? "connecting";
  const [savingStatus, setSavingStatus] = useState<DocSavingStatusType>(null);
  const { lastOpAt, lastOpSavedAt, opError } = useAppSelector(selectShareDBOpStatus);
  const delayTimeout = useRef<NodeJS.Timeout>(null);
  const showToastTimeout = useRef<NodeJS.Timeout>(null);
  const minUiFlickerTimeout = useRef<NodeJS.Timeout>(null);
  const isOverwriteError = opError === AUTH_ERRORS.OVERWRITE_DISALLOWED;

  const connectionIndicatorStatus = getOverallConnectionStatus(
    shareDBStatus,
    pubsubStatus,
    savingStatus,
    opError,
    isOverwriteError,
  );
  const iconState = WEBSOCKET_STATE_TO_ICON_STATE[connectionIndicatorStatus];

  useEffect(() => {
    if (!lastOpAt) {
      return;
    }
    if (delayTimeout.current) {
      clearTimeout(delayTimeout.current);
    }
    if (minUiFlickerTimeout.current) {
      clearTimeout(minUiFlickerTimeout.current);
    }
    if (lastOpSavedAt && lastOpAt < lastOpSavedAt) {
      minUiFlickerTimeout.current = setTimeout(() => {
        setSavingStatus(null);
      }, 1000);
    } else {
      setSavingStatus((status) => status || SAVING_STATUS.SAVING);
      delayTimeout.current = setTimeout(() => {
        setSavingStatus(SAVING_STATUS.DELAYED);
      }, SHAREDB_DELAY_THRESHOLD_MS);
    }
  }, [lastOpSavedAt, lastOpAt]);

  useEffect(() => {
    if (showToastTimeout.current) {
      clearTimeout(showToastTimeout.current);
    }
    toast.dismiss(ERROR_TOAST_ID);

    // We only show toasts for op and connection errors
    if (iconState !== "disconnected") {
      return;
    }

    // We only show toasts if it's not the common overwrite error that we don't care about
    if (isOverwriteError) {
      return;
    }

    // Determine which connection is disconnected to use appropriate delay
    // Only check ShareDB if it's available (not null)
    const isShareDBDisconnected = shareDBStatus !== null && isDisconnectedStatus(shareDBStatus);
    const isPubsubDisconnected = isDisconnectedStatus(pubsubStatus);
    const disconnectDelay = isShareDBDisconnected
      ? SHAREDB_DISCONNECT_TOAST_DELAY_MS
      : PUBSUB_DISCONNECT_TOAST_DELAY_MS;

    if (isShareDBDisconnected) {
      logInfoWithDebugFlag("debug_sharedb", "Shared DB Websocket disconnected", {
        userId: selectCurrentUserId(store.getState()),
        windowId: getWindowId(),
        shareDBStatus,
      });
    }

    if (isPubsubDisconnected) {
      logInfoWithDebugFlag("debug_pubsub", "Pubsub Websocket disconnected", {
        userId: selectCurrentUserId(store.getState()),
        windowId: getWindowId(),
        pubsubStatus,
      });
    }

    setSavingStatus(null);

    const toastText = opError ? t("error.toasts.save_failure") : t("websocket.disconnected");
    // We delay showing the toast so that minimal flickers in connection do not
    // cause a flickering toast banner. Use shorter delay for ShareDB (critical),
    // longer delay for pubsub (less critical).
    showToastTimeout.current = setTimeout(() => {
      logInfoWithDebugFlag("debug_pubsub", "Showing Websocket disconnected toast ", {
        userId: selectCurrentUserId(store.getState()),
        windowId: getWindowId(),
        shareDBStatus,
        pubsubStatus,
      });
      toast.error(toastText, {
        toastId: "websocket-disconnected",
        autoClose: false,
        closeButton: false,
      });
    }, disconnectDelay);
  }, [opError, iconState, isOverwriteError, shareDBStatus, pubsubStatus]);

  return (
    <div className={className}>
      <ConnectionIcon iconState={iconState} />
    </div>
  );
};
